/**
 * Text/binary decoders used by the older SERPRO/VIO QR versions, transcribed
 * from the VIO app (`c5.a.a` = basE91, `c5.b.a` = a 7-bit accented alphabet).
 *
 * NOTE: only the 6-bit path (v2/v5) is verified against real documents. These
 * two decoders are used by versions 1, 3 and 4, for which no reference sample
 * was available — see the design spec's "unverified versions" note.
 */
import { readBits } from "./cnh";

/** basE91 alphabet used by `c5.a.a` (91 symbols; note the ordering differs from stock basE91). */
export const BASE91_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!#$%&()*+,./:;<=>?@[]^_`{|}~\"";

const BASE91_RADIX = BASE91_ALPHABET.length; // 91

/** byte value → basE91 symbol index, or -1 if the byte is not an alphabet symbol. */
const BASE91_REVERSE: Int8Array = (() => {
  const table = new Int8Array(256).fill(-1);
  for (let i = 0; i < BASE91_ALPHABET.length; i++) {
    table[BASE91_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/**
 * Decodes basE91-encoded bytes back to their original bytes, following the
 * app's `c5.a.a`: symbols are consumed in pairs into a value `hi*91 + lo`, which
 * contributes 13 or 14 bits (14 when its low 13 bits are ≤ 88) to a little-endian
 * bit accumulator that is drained a byte at a time.
 */
export function decodeBase91(data: Uint8Array): Uint8Array {
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  let pending = -1;

  for (const byte of data) {
    const idx = BASE91_REVERSE[byte]!;
    if (idx === -1) continue; // ignore non-alphabet bytes (whitespace, separators)
    if (pending === -1) {
      pending = idx;
    } else {
      const val = idx * BASE91_RADIX + pending;
      acc |= val << bits;
      bits += (val & 8191) <= 88 ? 14 : 13;
      do {
        out.push(acc & 0xff);
        acc >>>= 8;
        bits -= 8;
      } while (bits > 7);
      pending = -1;
    }
  }
  if (pending !== -1) {
    out.push(((pending << bits) | acc) & 0xff);
  }
  return Uint8Array.from(out);
}

/** 7-bit alphabet used by `c5.b.a` (index 0 is a space; includes Portuguese accents). */
export const ACCENTED_ALPHABET =
  " ABCÇDEFGHIJKLMNOPQRSTUVWXYZabcçdefghijklmnopqrstuvwxyz0123456789" +
  "áàéíóúüñÁÀÉÍÓÚÜÑ" +
  "ÃãÂâÔôÕõ=+-/\\*_|()[]{}<>#%&@'\".:;,!?$\n~^" +
  "êÊºª§";

/**
 * Decodes a 7-bit-per-symbol bitstream (MSB-first) into text using
 * {@link ACCENTED_ALPHABET}, following the app's `c5.b.a`.
 *
 * @throws {Error} If a symbol index falls outside the alphabet.
 */
export function decode7bitAccented(data: Uint8Array): string {
  let result = "";
  let bitPos = 0;
  const totalBits = data.length * 8;
  while (bitPos + 7 <= totalBits) {
    const idx = readBits(data, bitPos, 7);
    bitPos += 7;
    if (idx < 0 || idx >= ACCENTED_ALPHABET.length) {
      throw new Error(`7-bit symbol ${idx} not found in alphabet`);
    }
    result += ACCENTED_ALPHABET[idx];
  }
  return result;
}
